/**
 * Deduplicator Module
 *
 * Removes duplicate records from normalized arrays before aggregation.
 * A record is considered a duplicate if:
 * 1. It has the same `id` as a previously seen record, OR
 * 2. It has the same composite key (recordedAt + creatorName + payload content)
 *
 * When duplicates are found, the first occurrence is kept and subsequent
 * duplicates are discarded.
 */

import type { DeduplicationResult } from '../types';

/**
 * Minimum interface that all deduplicatable records must satisfy.
 * All normalized record types (NormalizedInputRecord, NormalizedOutputRecord,
 * NormalizedNoteRecord) extend this shape.
 */
export interface DeduplicatableRecord {
  id: number;
  recordedAt: string;
  creatorName: string;
}

/**
 * Removes duplicate records from the input array.
 *
 * Deduplication is performed in two ways:
 * 1. Same `id` → duplicate (first occurrence kept)
 * 2. Same composite key (recordedAt + creatorName + payload fingerprint) → duplicate
 *
 * The optional `getPayloadKey` parameter allows callers to customize how the
 * payload fingerprint is computed. If not provided, the entire record is
 * serialized (excluding the `id` field) as the fingerprint.
 *
 * @param records - Array of normalized records to deduplicate
 * @param getPayloadKey - Optional function to extract a payload fingerprint string from a record
 * @returns DeduplicationResult with unique records and count of duplicates removed
 */
export function deduplicate<T extends DeduplicatableRecord>(
  records: T[],
  getPayloadKey?: (record: T) => string
): DeduplicationResult<T> {
  const seenIds = new Set<number>();
  const seenCompositeKeys = new Set<string>();
  const unique: T[] = [];
  let duplicatesRemoved = 0;

  for (const record of records) {
    // Check id uniqueness
    if (seenIds.has(record.id)) {
      duplicatesRemoved++;
      continue;
    }

    // Build composite key for content-based deduplication
    const payloadKey = getPayloadKey
      ? getPayloadKey(record)
      : defaultPayloadKey(record);
    const compositeKey = `${record.recordedAt}|${record.creatorName}|${payloadKey}`;

    if (seenCompositeKeys.has(compositeKey)) {
      duplicatesRemoved++;
      continue;
    }

    seenIds.add(record.id);
    seenCompositeKeys.add(compositeKey);
    unique.push(record);
  }

  return { unique, duplicatesRemoved };
}

/**
 * Default payload key generator that serializes the record excluding the `id` field.
 * This ensures two records with different ids but identical content are still
 * detected as duplicates via the composite key check.
 */
function defaultPayloadKey<T extends DeduplicatableRecord>(record: T): string {
  const { id: _id, ...rest } = record;
  return JSON.stringify(rest);
}
